/**
 * Pacing for AI calls that go through the backend gateway (/api/ai/*).
 *
 * Why it exists. Live, 2026-09-20, the first real 16-project run on the cloud:
 * projects ran strictly one at a time, and OpenRouter still answered 429, the
 * gateway quarantined every key, and a project was failed. ai-api.js has had a
 * five-call limiter since April, but it sits inside fetchWithRateLimitRetry,
 * which is the browser-direct OpenRouter path. Production never takes that
 * path: every call goes through the gateway, and the gateway path took no slot
 * at all. One project's Stage 1 (photo batches, PDF pages, video frames, eight
 * documents at once, contact extraction) therefore left the browser as a single
 * burst with nothing holding it back.
 *
 * Two jobs:
 *   1. At most N gateway calls in flight from this tab.
 *   2. Back-pressure. When the gateway says "rate limited" or "every key is
 *      cooling down" it sends a retry hint. No NEW call starts until that
 *      moment, and the refused call is tried again, so a momentary limit costs
 *      a short orderly pause instead of a failed stage and a restarted project.
 *
 * Nothing here is a timeout. A caller waits for a slot, or for the provider's
 * own cooldown, for as long as that takes; the wait ends early only when the
 * caller's AbortSignal fires (the operator pressed Stop). Callers must take the
 * slot BEFORE they start any transport timer, so time spent queued can never
 * be counted against a request that has not been sent yet.
 */

import { isGatewayPolicyRefusal } from './ai-error-handling';

const DEFAULT_MAX_IN_FLIGHT = 4;
const MIN_PAUSE_MS = 5_000;
// The gateway's soonest-key hint is 60s for a rate limit and 15s for a wobble.
// A hint far past that means every key is in the five-minute cooldown an
// authentication or credit failure produces. That needs the operator, so it is
// neither paused for nor retried here; batch-queue.js reports it.
const MAX_HINT_SECONDS = 90;
const DEFAULT_PAUSE_MS = 20_000;
const MAX_BACKPRESSURE_RETRIES = 2;

type Waiter = { start: () => void; cancel: (reason: unknown) => void };

let inFlight = 0;
let pausedUntil = 0;
const waiters: Waiter[] = [];
let resumeTimer: ReturnType<typeof setTimeout> | null = null;

function readStorage(key: string): string | null {
    try {
        return typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem(key) : null;
    } catch { return null; }
}

function disabled(): boolean {
    return !!readStorage('qm_disable_concurrency_cap');
}

function maxInFlight(): number {
    const raw = parseInt(readStorage('qm_max_concurrent_ai_gateway') || '', 10);
    return Number.isFinite(raw) && raw > 0 && raw <= 50 ? raw : DEFAULT_MAX_IN_FLIGHT;
}

function abortError(signal: AbortSignal): unknown {
    // Hand back the caller's own reason untouched, whatever its type. fetch()
    // rejects with signal.reason, and callers name their cancellations with
    // plain strings ('inspector-notes-focus') that the monitor reads back.
    const reason = (signal as AbortSignal & { reason?: unknown }).reason;
    if (reason !== undefined && reason !== null) return reason;
    const error = new Error('AI request cancelled before it was sent');
    error.name = 'AbortError';
    return error;
}

function pump(): void {
    const now = Date.now();
    if (now < pausedUntil) {
        if (!resumeTimer) {
            resumeTimer = setTimeout(() => { resumeTimer = null; pump(); }, pausedUntil - now);
        }
        return;
    }
    while (waiters.length > 0 && inFlight < maxInFlight()) {
        const next = waiters.shift()!;
        inFlight += 1;
        next.start();
    }
}

function acquire(signal?: AbortSignal | null): Promise<void> {
    if (signal?.aborted) return Promise.reject(abortError(signal));
    return new Promise<void>((resolve, reject) => {
        const waiter: Waiter = {
            start: () => { signal?.removeEventListener?.('abort', onAbort); resolve(); },
            cancel: reject,
        };
        const onAbort = () => {
            const index = waiters.indexOf(waiter);
            if (index >= 0) waiters.splice(index, 1);
            reject(abortError(signal!));
        };
        signal?.addEventListener?.('abort', onAbort, { once: true });
        waiters.push(waiter);
        pump();
    });
}

function release(): void {
    inFlight = Math.max(0, inFlight - 1);
    pump();
}

/** The gateway refused for load, with a hint short enough to be worth waiting for. */
export function providerBackpressureMs(error: unknown): number {
    const err = (error && typeof error === 'object' ? error : {}) as Record<string, unknown>;
    const code = String(err.code || '');
    // The gateway's policy refusals (the per-call cost limit, the AI allow-list,
    // the daily and monthly spend limits) are sent as HTTP 429 but are not load:
    // the same request is refused every time, so pausing the tab or sending it
    // again only delays the answer.
    if (isGatewayPolicyRefusal(err)) return 0;
    const status = Number(err.status || err.statusCode || 0);
    const upstream = Number(err.upstreamStatusCode || 0);
    const limited = code === 'upstream_429' || code === 'all_keys_quarantined' || status === 429 || upstream === 429;
    if (!limited) return 0;
    const hint = Number(err.retryAfterSeconds);
    if (Number.isFinite(hint) && hint > MAX_HINT_SECONDS) return 0;
    const hintedMs = Number.isFinite(hint) && hint > 0 ? hint * 1000 + 2_000 : DEFAULT_PAUSE_MS;
    return Math.max(MIN_PAUSE_MS, hintedMs);
}

function pauseNewCalls(ms: number): void {
    pausedUntil = Math.max(pausedUntil, Date.now() + ms);
}

function sleepUntilResumed(signal?: AbortSignal | null): Promise<void> {
    const remaining = pausedUntil - Date.now();
    if (remaining <= 0) return Promise.resolve();
    if (signal?.aborted) return Promise.reject(abortError(signal));
    return new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => { signal?.removeEventListener?.('abort', onAbort); resolve(); }, remaining);
        const onAbort = () => { clearTimeout(timer); reject(abortError(signal!)); };
        signal?.addEventListener?.('abort', onAbort, { once: true });
    });
}

export interface AiGatewaySlotOptions {
    signal?: AbortSignal | null;
    /** False for an evaluation run that must prove a single paid attempt. */
    retryOnBackpressure?: boolean;
}

/**
 * Run one gateway call under the limiter. `task` must create its own transport
 * timer INSIDE the callback, so the timer starts when the request is sent.
 */
export async function withAiGatewaySlot<T>(task: () => Promise<T>, options: AiGatewaySlotOptions = {}): Promise<T> {
    if (disabled()) return task();
    const { signal = null, retryOnBackpressure = true } = options;
    for (let attempt = 0; ; attempt += 1) {
        await acquire(signal);
        let pauseMs = 0;
        try {
            return await task();
        } catch (error) {
            pauseMs = providerBackpressureMs(error);
            if (pauseMs > 0) pauseNewCalls(pauseMs);
            if (pauseMs === 0 || !retryOnBackpressure || attempt >= MAX_BACKPRESSURE_RETRIES || signal?.aborted) throw error;
        } finally {
            release();
        }
        // Slot released first: a paused call must not hold capacity while it waits.
        await sleepUntilResumed(signal);
    }
}

export function aiGatewayLimiterState(): { inFlight: number; waiting: number; pausedForMs: number } {
    return { inFlight, waiting: waiters.length, pausedForMs: Math.max(0, pausedUntil - Date.now()) };
}

export function __resetAiGatewayLimiterForTests(): void {
    inFlight = 0;
    pausedUntil = 0;
    waiters.splice(0, waiters.length);
    if (resumeTimer) { clearTimeout(resumeTimer); resumeTimer = null; }
}
